import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, RefreshCw, ShieldCheck, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

export default function FacebookReviewQueuePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mod/facebook-signals?status=review_required&limit=50`, { headers: modHeaders() });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (fingerprint, status) => {
    setWorking(fingerprint);
    try {
      await axios.post(`${API}/mod/facebook-signals/${fingerprint}/review`, { status }, { headers: modHeaders() });
      toast.success(status === "accepted" ? "Signal godkänd." : "Signal avvisad.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte uppdatera signalen.");
    } finally {
      setWorking(null);
    }
  };

  return (
    <section className="bg-parchment border border-warm-border rounded-sm p-4 mb-3" data-testid="facebook-review-queue">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={17} className="text-pine" />
          <div>
            <h3 className="font-heading text-base font-medium text-ink">Signaler att granska</h3>
            <p className="text-[11px] text-bark">Godkännande här publicerar inget automatiskt; det markerar bara signalens granskningsstatus.</p>
          </div>
        </div>
        <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-1 text-xs text-pine border border-pine px-2.5 py-1 rounded-sm disabled:opacity-50">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Uppdatera
        </button>
      </div>

      {loading ? (
        <p className="text-xs text-bark flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Laddar…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-bark">Ingen signal väntar på granskning.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <article key={item.fingerprint} className="bg-white border border-warm-border rounded-sm p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-ink">{item.title || item.source_id || "Signal"}</p>
                  <p className="text-[11px] text-bark mt-0.5">{item.signal_type}{item.event_date ? ` · ${item.event_date}` : ""}{item.place ? ` · ${item.place}` : ""}</p>
                  <p className="text-xs text-ink/80 mt-2 whitespace-pre-wrap line-clamp-4">{item.text}</p>
                </div>
                <div className="flex gap-1">
                  <button type="button" disabled={working === item.fingerprint} onClick={() => decide(item.fingerprint, "accepted")} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-pine text-pine rounded-sm disabled:opacity-50">
                    <Check size={12} /> Godkänn
                  </button>
                  <button type="button" disabled={working === item.fingerprint} onClick={() => decide(item.fingerprint, "rejected")} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-falu text-falu rounded-sm disabled:opacity-50">
                    <X size={12} /> Avvisa
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
